import { useState, useRef } from "react";
import { Popover, Tag, Typography, Divider, Button } from "antd";
import {
  FaThermometerHalf,
  FaBolt,
  FaTint,
  FaMicrochip,
  FaWind,
  FaTrash,
} from "react-icons/fa";
import dayjs from "dayjs";

const { Text } = Typography;

// Status colors — industrial indicator lamp palette
const STATUS = {
  active:   { ring: "#16A34A", icon: "#16A34A", tag: "success",  label: "Operativo" },
  alert:    { ring: "#DC2626", icon: "#DC2626", tag: "error",    label: "Alerta"    },
  inactive: { ring: "#9CA3AF", icon: "#9CA3AF", tag: "default",  label: "Inactivo"  },
};

function resolveStatus(pin, metrics) {
  if (!pin.isActive) return "inactive";
  if (metrics?.some((m) => m.status === "FAIL")) return "alert";
  return "active";
}

function getSensorIcon(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("temp")) return <FaThermometerHalf />;
  if (n.includes("energ") || n.includes("power") || n.includes("watt")) return <FaBolt />;
  if (n.includes("flow") || n.includes("agua") || n.includes("water")) return <FaTint />;
  if (n.includes("aire") || n.includes("air") || n.includes("co2") || n.includes("hum")) return <FaWind />;
  return <FaMicrochip />;
}

// Popover content — engineering info card
function PinCard({ pin, metrics, status, isEditMode, onRemove, onClose }) {
  const s = STATUS[status];
  const lastTimestamp =
    metrics?.[0]?.timestamp || metrics?.[0]?.time || metrics?.[0]?._time;

  return (
    <div style={{ width: 260 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#F3F4F6",
            border: `2px solid ${s.ring}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: s.icon,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          {getSensorIcon(pin.deviceName)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            strong
            style={{
              display: "block",
              fontSize: 13,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pin.deviceName}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {pin.storeName || pin.locationName || "—"}
          </Text>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Tag color={s.tag} style={{ margin: 0, fontSize: 11 }}>
          {s.label}
        </Tag>
        {lastTimestamp && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(lastTimestamp).format("DD/MM/YY HH:mm")}
          </Text>
        )}
      </div>

      {/* Metrics */}
      {metrics && metrics.length > 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {metrics.slice(0, 5).map((m) => (
              <div
                key={m.field}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text
                  type="secondary"
                  style={{ fontSize: 12, textTransform: "capitalize" }}
                >
                  {(m.field || "").replace(/_/g, " ")}
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: "monospace",
                    fontWeight: 600,
                    color: m.status === "FAIL" ? "#DC2626" : "#111827",
                  }}
                >
                  {m.value} {m.unit}
                </Text>
              </div>
            ))}
          </div>
        </>
      )}

      {metrics && metrics.length === 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Sin métricas disponibles
          </Text>
        </>
      )}

      {isEditMode && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <Button
            danger
            size="small"
            icon={<FaTrash style={{ fontSize: 11, marginRight: 5 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              onRemove(pin.id);
            }}
            block
            style={{ fontSize: 12 }}
          >
            Quitar del diagrama
          </Button>
        </>
      )}
    </div>
  );
}

// P&ID instrument bubble pin
export const SensorPin = ({ pin, metrics, isEditMode, onRemove, onDragStart }) => {
  const [open, setOpen] = useState(false);
  const didDragRef = useRef(false);

  const status = resolveStatus(pin, metrics);
  const s = STATUS[status];

  const handleMouseDown = (e) => {
    if (!isEditMode) return;
    e.stopPropagation();
    didDragRef.current = false;
    onDragStart(e, pin.id, () => {
      didDragRef.current = true;
    });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <div
      style={{
        position: "absolute",
        left: `calc(${pin.x}% - 19px)`,
        top: `calc(${pin.y}% - 19px)`,
        zIndex: 10,
        userSelect: "none",
      }}
    >
      <Popover
        content={
          <PinCard
            pin={pin}
            metrics={metrics}
            status={status}
            isEditMode={isEditMode}
            onRemove={onRemove}
            onClose={() => setOpen(false)}
          />
        }
        title={null}
        open={open}
        onOpenChange={(v) => { if (!v) setOpen(false); }}
        trigger="click"
        placement="top"
        overlayStyle={{ maxWidth: 280 }}
      >
        {/* P&ID instrument bubble */}
        <div
          className={status === "alert" ? "sensor-pin--alert" : ""}
          onClick={handleClick}
          onMouseDown={isEditMode ? handleMouseDown : undefined}
          title={pin.deviceName}
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#FFFFFF",
            border: `3px solid ${s.ring}`,
            boxShadow: status !== "alert"
              ? "0 2px 8px rgba(0,0,0,0.15)"
              : undefined,
            cursor: isEditMode ? "grab" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: s.icon,
            fontSize: 15,
            transition: "transform 0.15s, border-color 0.2s",
          }}
        />
      </Popover>

      {/* Instrument tag label below bubble */}
      <div className="pin-label" title={pin.deviceName}>
        {pin.deviceName}
      </div>
    </div>
  );
};
